import * as THREE from 'three';
import React, { useMemo, useRef, useEffect } from 'react';
import { compute3DMeshDataAndPosition, loadTexture } from '../../utils';
import * as constantsViewer from '../../constants';
import {
  createReactThreeGeometry,
  createReactThreeMaterial,
} from '../../reactThreeUtils';
import { getMaterialData, manageOnClickComponent } from '../../helpers';
import { useSpring, a } from 'react-spring/three';
import textureMdfNatural from 'assets/images/textures/mdfnatural2.jpg';
import data from './data/upperShelf';
import { onPointerDown, onPointerUp } from '../../helpers';
import { useDispatch } from 'react-redux';
import { applyPaintColor } from 'containers/PaintPanel/actions';

export function UpperShelf({
  upperShelf = null,
  cabinetId,
  configuratorMode,
  cabinetWidth = 1000,
  cabinetDepth = 420,
  cabinetHeight = 468,
  unselected = false,
}) {
  const mesh = useRef();
  const dispatch = useDispatch();

  const baseData = useMemo(() => compute3DMeshDataAndPosition(data), [data]);

  const textures = useMemo(
    () => ({
      textureMdfNaturalLoaded: loadTexture(textureMdfNatural),
    }),
    [textureMdfNatural],
  );

  const materialData = getMaterialData(upperShelf, textures);

  const [spring, set] = useSpring(() => ({
    position: getPosition(
      baseData.position,
      cabinetHeight,
      cabinetWidth,
      cabinetDepth,
    ),
    scale: getScale(baseData.size, baseData.size, cabinetWidth, cabinetDepth),
    immediate: !constantsViewer.REACT_SPRING_ANIMATIONS_ACTIVE,
    config: constantsViewer.REACT_SPRING_ANIMATIONS_CONF,
  }));

  useEffect(() => {
    const self = mesh.current;
    if (self) {
      self.geometry.computeBoundingBox();
      const size = new THREE.Vector3();
      self.geometry.boundingBox.getSize(size);

      set({
        position: getPosition(baseData.position, cabinetHeight),
        scale: getScale(baseData.size, size, cabinetWidth, cabinetDepth),
      });
    }
  }, [cabinetWidth, cabinetDepth, cabinetHeight]);

  return (
    <a.mesh
      ref={mesh}
      receiveShadow
      castShadow
      onPointerDown={e => onPointerDown(e)}
      onPointerUp={e =>
        onPointerUp(e, e => {
          manageOnClickComponent(
            e,
            unselected,
            upperShelf,
            cabinetId,
            dispatch,
            configuratorMode,
          );
        })
      }
      position={spring.position}
      scale={spring.scale}
      renderOrder={constantsViewer.ZORDER_UPPER_SHELF}
    >
      {createReactThreeGeometry(baseData)}
      {createReactThreeMaterial(materialData, unselected)}
    </a.mesh>
  );
}

function getPosition(basePosition, cabinetHeight) {
  const cabinetDiffHeight =
    cabinetHeight - constantsViewer.MODEL_CABINET_HEIGHT_WITH_UPPER_SHELF;
  return [
    basePosition.x,
    basePosition.y + cabinetDiffHeight / 2,
    basePosition.z,
  ];
}

function getScale(baseSize, size, cabinetWidth, cabinetDepth) {
  const spaceWidthNoScale = constantsViewer.MODEL_CABINET_WIDTH - baseSize.x;
  const spaceDepthNoScale = constantsViewer.MODEL_CABINET_DEPTH - baseSize.z;
  return [
    (cabinetWidth - spaceWidthNoScale) / size.x,
    1,
    (cabinetDepth - spaceDepthNoScale) / size.z,
  ];
}
